import { useCallback, useEffect, useState } from "react"
import { apiRequest } from "@/lib/api-request"
import { getApiUrl } from "@/lib/api-config"
import { DEFAULT_WORKING_DAYS, type WorkingDaysConfig } from "./schedule-date-engine"

export const WORKING_DAYS_SETTING_KEY = "schedule_working_days"

/**
 * Single source of truth for the company's working days (spec: "Company
 * Settings -> Working Days -> Schedule date engine"). Reads the
 * setting through the backend (GET /api/company-settings/:key answers the
 * stored value or the code default), falls back to the Mon-Fri default when
 * the request fails. Both Settings -> Company's Working Days editor
 * and every Schedule view that needs working-day math (Gantt, List,
 * Calendar) use this SAME hook -- one fetch, one cache, one place this
 * setting is read from.
 */
export function useCompanyWorkingDays(companyId?: string | null) {
  const [workingDays, setWorkingDaysState] = useState<WorkingDaysConfig>(DEFAULT_WORKING_DAYS)
  const [loading, setLoading] = useState(true)

  const refetch = useCallback(async () => {
    if (!companyId) {
      setWorkingDaysState(DEFAULT_WORKING_DAYS)
      setLoading(false)
      return
    }
    setLoading(true)
    try {
      const data = await apiRequest<{ key: string; value: Partial<WorkingDaysConfig> | null }>(`api/company-settings/${WORKING_DAYS_SETTING_KEY}`)
      setWorkingDaysState(data?.value ? { ...DEFAULT_WORKING_DAYS, ...data.value } : DEFAULT_WORKING_DAYS)
    } catch {
      setWorkingDaysState(DEFAULT_WORKING_DAYS)
    }
    setLoading(false)
  }, [companyId])

  useEffect(() => {
    refetch()
  }, [refetch])

  const saveWorkingDays = useCallback(
    async (next: WorkingDaysConfig, userId?: string) => {
      if (!companyId || !userId) return false
      const previous = workingDays
      setWorkingDaysState(next) // optimistic
      try {
        const response = await fetch(getApiUrl(`api/company-settings/${WORKING_DAYS_SETTING_KEY}`), {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ user_id: userId, company_id: companyId, value: next }),
        })
        if (!response.ok) {
          setWorkingDaysState(previous)
          return false
        }
        return true
      } catch (err) {
        console.error("[useCompanyWorkingDays] failed to save", err)
        setWorkingDaysState(previous)
        return false
      }
    },
    [companyId, workingDays]
  )

  return { workingDays, loading, saveWorkingDays, refetch }
}
